import { writable } from 'svelte/store';

export const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max);
}

export const debounce = (func, wait) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const b64toBlob = function(b64Data, contentType, sliceSize) {
  contentType = contentType || '';
  sliceSize = sliceSize || 512;

  var byteCharacters = atob(b64Data);
  var byteArrays = [];

  for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      var slice = byteCharacters.slice(offset, offset + sliceSize);

      var byteNumbers = new Array(slice.length);
      for (var i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
      }

      var byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
  }

  var blob = new Blob(byteArrays, {type: contentType});
  return blob;
}

export const createToggleStore = (params = {}) => {
  let store = writable({
    open: false,
    data: null,
    ...params
  });

  const { subscribe, set, update } = store;

  return {
    subscribe,
    set,
    update,
    open : (data, extra = {}) => {
      update(store => {
        store.open = true;
        store.data = data;
        for (const [key, value] of Object.entries(extra)) {
          store[key] = value;
        }
        return store;
      });
    },
    close: () => {
      update(store => {
        store.open = false;
        store.data = null;
        for (const [key, value] of Object.entries(params)) {
          store[key] = value;
        }
        return store;
      });
    }
  }
}

export const stringToColor = function(str) {
  let hash = 0;
  if(!str){
    return '#000'
  }
  for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
      let value = (hash >> (i * 8)) & 0xFF;
      color += ('00' + value.toString(16)).substr(-2);
  }
  return color;
}

export const stopBodyScroll = () => {
  document.body.classList.add('overflow-hidden');
}

export const allowBodyScroll = () => {
  document.body.classList.remove('overflow-hidden');
}

export const timestampToString = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString();
}

/**
 * Copy text to the clipboard. Uses the Clipboard API when available; falls back to
 * execCommand for non-secure origins (e.g. http://*.local domains) where clipboard.writeText fails.
 * @returns {Promise<boolean>}
 */
export async function copyToClipboard(text) {
  if (text == null || text === '') {
    return false;
  }
  const str = String(text);
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(str);
      return true;
    }
  } catch (err) {
    console.warn('navigator.clipboard.writeText failed, trying fallback', err);
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = str;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.top = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (err2) {
    console.warn('execCommand copy failed', err2);
    return false;
  }
}


export const colorDetect = (node, opts) => {
  function adjustTextColor(element) {
    // Get the element's background color
    let bgColor = window.getComputedStyle(element, null).getPropertyValue('background-color');
    // Call lightOrDark function to get the brightness (light or dark)
    let brightness = lightOrDark(bgColor);
    console.log(brightness);
    // If the background color is dark, add the light-text class to it
    if (brightness === 'dark') {
      element.classList.remove('dark-text');
      element.classList.add('light-text');
    } else {
      element.classList.remove('light-text');
      element.classList.add('dark-text');
    }
  }

  adjustTextColor(node);
};

export function lightOrDark(color) {
  let r, g, b, hsp;
  // Check the format of the color, HEX or RGB?
  if (color.match(/^rgb/)) {
    // If HEX --> store the red, green, blue values in separate variables
    color = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);

    r = parseInt(color[1]);
    g = parseInt(color[2]);
    b = parseInt(color[3]);
  } else {
    // If RGB --> Convert it to HEX: http://gist.github.com/983661
    color = +('0x' + color.slice(1).replace(color.length < 5 && /./g, '$&$&'));

    r = color >> 16;
    g = (color >> 8) & 255;
    b = color & 255;
  }

  // change the equation.. it was not perfectly accurate.. this is better
  var luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
  console.log({luma})
  if (luma > 200) {
    return 'light';
  } else {
    return 'dark';
  }
}

export const colorDetectDynamic = (node, { watchedClasses = [] } = {}) => {
  colorDetect(node);

  let prevClassNames = node.className.split(' ');

  let observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        let newClasses = node.className.split(' ');

        // Filter out 'light-text' and 'dark-text' classes
        let newFiltered = newClasses.filter((c) => c !== 'light-text' && c !== 'dark-text');
        let prevFiltered = prevClassNames.filter((c) => c !== 'light-text' && c !== 'dark-text');

        // If any of the watched classes were added or removed, run colorDetect
        if (watchedClasses.some((c) => newFiltered.includes(c) !== prevFiltered.includes(c))) {
          colorDetect(node);
        }

        // Update the stored class list
        prevClassNames = newClasses;
      }
    });
  });

  observer.observe(node, { attributes: true });

  return {
    destroy() {
      observer.disconnect();
    },
  };
};

export function changeHue(rgb, degree) {
  var hsl = rgbToHSL(rgb);
  hsl.h += degree;
  if (hsl.h > 360) {
    hsl.h -= 360;
  }
  else if (hsl.h < 0) {
    hsl.h += 360;
  }
  return hslToRGB(hsl);
}

// exepcts a string and returns an object
function rgbToHSL(rgb) {
  // strip the leading # if it's there
  rgb = rgb.replace(/^\s*#|\s*$/g, '');

  // convert 3 char codes --> 6, e.g. `E0F` --> `EE00FF`
  if(rgb.length == 3){
    rgb = rgb.replace(/(.)/g, '$1$1');
  }

  var r = parseInt(rgb.substr(0, 2), 16) / 255,
      g = parseInt(rgb.substr(2, 2), 16) / 255,
      b = parseInt(rgb.substr(4, 2), 16) / 255,
      cMax = Math.max(r, g, b),
      cMin = Math.min(r, g, b),
      delta = cMax - cMin,
      l = (cMax + cMin) / 2,
      h = 0,
      s = 0;

  if (delta == 0) {
    h = 0;
  }
  else if (cMax == r) {
    h = 60 * (((g - b) / delta) % 6);
  }
  else if (cMax == g) {
    h = 60 * (((b - r) / delta) + 2);
  }
  else {
    h = 60 * (((r - g) / delta) + 4);
  }

  if (delta == 0) {
    s = 0;
  }
  else {
    s = (delta/(1-Math.abs(2*l - 1)))
  }

  return {
    h: h,
    s: s,
    l: l
  }
}

// expects an object and returns a string
function hslToRGB(hsl) {
  var h = hsl.h,
      s = hsl.s,
      l = hsl.l,
      c = (1 - Math.abs(2*l - 1)) * s,
      x = c * ( 1 - Math.abs((h / 60 ) % 2 - 1 )),
      m = l - c/ 2,
      r, g, b;

  if (h < 60) {
    r = c;
    g = x;
    b = 0;
  }
  else if (h < 120) {
    r = x;
    g = c;
    b = 0;
  }
  else if (h < 180) {
    r = 0;
    g = c;
    b = x;
  }
  else if (h < 240) {
    r = 0;
    g = x;
    b = c;
  }
  else if (h < 300) {
    r = x;
    g = 0;
    b = c;
  }
  else {
    r = c;
    g = 0;
    b = x;
  }

  r = normalize_rgb_value(r, m);
  g = normalize_rgb_value(g, m);
  b = normalize_rgb_value(b, m);

  return rgbToHex(r,g,b);
}

function normalize_rgb_value(color, m) {
  color = Math.floor((color + m) * 255);
  if (color < 0) {
    color = 0;
  }
  return color;
}

function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Samples values from an array at regular intervals
 * @param {Array} array - The input array to sample from
 * @param {number} interval - The interval to sample at (e.g., 5 for every 5th element)
 * @param {number} startIndex - Optional starting index (default: 0)
 * @returns {Array} - Array of sampled values
 */
export const sampleArrayAtInterval = (array, interval, startIndex = 0) => {
  if (!Array.isArray(array) || interval <= 0) {
    return [];
  }
  
  const result = [];
  for (let i = startIndex; i < array.length; i += interval) {
    result.push(array[i]);
  }
  
  return result;
};

/**
 * Samples time-series data at regular time intervals
 * @param {Array} dataArray - Array of data points (1 per second)
 * @param {number} secondsInterval - Interval in seconds (e.g., 5 for every 5 seconds)
 * @param {number} startOffset - Optional start offset in seconds (default: 0)
 * @returns {Array} - Array of sampled data points
 */
export const sampleTimeSeriesData = (dataArray, secondsInterval, startOffset = 0) => {
  if (!Array.isArray(dataArray) || secondsInterval <= 0) {
    return [];
  }
  
  // Since data is 1 item per second, we can use the interval directly
  return sampleArrayAtInterval(dataArray, secondsInterval, startOffset);
};

export const HR_SPIKE_WINDOW = 3;

function _median(values) {
  const ordered = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(ordered.length / 2);
  return ordered.length % 2 ? ordered[mid] : (ordered[mid - 1] + ordered[mid]) / 2;
}

/** Median-filter HR (same rule as Django/app). Peak and chart share this series. */
export const cleanHrValues = (values, window = HR_SPIKE_WINDOW) => {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }
  const n = values.length;
  const cleaned = values.slice();
  for (let i = 0; i < n; i++) {
    const raw = values[i];
    if (raw == null || raw <= 0) continue;
    const start = Math.max(0, i - window);
    const end = Math.min(n, i + window + 1);
    const neighbors = [];
    for (let j = start; j < end; j++) {
      if (values[j] != null && values[j] > 0) neighbors.push(values[j]);
    }
    if (!neighbors.length) continue;
    cleaned[i] = Math.round(_median(neighbors));
  }
  return cleaned;
};

export const cleanHrmChartPoints = (points) => {
  if (!Array.isArray(points) || !points.length) return points;
  const cleaned = cleanHrValues(points.map((p) => p && p.data));
  return points.map((p, i) => ({ ...p, data: cleaned[i] }));
};

/**
 * Downsample {data, label} points by keeping min and max in each stride bucket
 * (time order preserved) so recorded peaks/valleys stay on the chart.
 */
export const sampleTimeSeriesPreserveExtrema = (dataArray, interval) => {
  if (!Array.isArray(dataArray) || interval <= 0) {
    return [];
  }
  if (interval <= 1) {
    return dataArray.slice();
  }

  const result = [];
  for (let start = 0; start < dataArray.length; start += interval) {
    const bucket = dataArray.slice(start, start + interval);
    let minI = -1;
    let maxI = -1;
    for (let i = 0; i < bucket.length; i++) {
      const v = bucket[i] && bucket[i].data;
      if (v === null || v === undefined) continue;
      if (minI < 0 || v < bucket[minI].data) minI = i;
      if (maxI < 0 || v > bucket[maxI].data) maxI = i;
    }
    if (minI < 0) continue;
    const first = Math.min(minI, maxI);
    const second = Math.max(minI, maxI);
    result.push(bucket[first]);
    if (second !== first) {
      result.push(bucket[second]);
    }
  }
  return result;
};

/**
 * After smoothing, put original global min/max values back at matching labels
 * so the plotted peak still matches the recorded sample max.
 */
export const restoreSeriesExtrema = (processed, originalPoints) => {
  if (!processed || !Array.isArray(processed.data) || !Array.isArray(originalPoints) || !originalPoints.length) {
    return processed;
  }

  let minPt = null;
  let maxPt = null;
  originalPoints.forEach((pt) => {
    if (pt == null || pt.data == null) return;
    if (!minPt || pt.data < minPt.data) minPt = pt;
    if (!maxPt || pt.data > maxPt.data) maxPt = pt;
  });

  const restore = (pt) => {
    if (!pt || pt.label == null) return;
    const idx = processed.labels.indexOf(pt.label);
    if (idx >= 0) {
      processed.data[idx] = pt.data;
    }
  };
  restore(minPt);
  restore(maxPt);
  return processed;
};

/**
 * Simple moving average smoothing
 * @param {Array} data - Array of numeric values
 * @param {number} windowSize - Size of the moving window (default: 3)
 * @returns {Array} - Smoothed data array
 */
export const movingAverageSmooth = (data, windowSize = 3) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }
  
  const smoothed = [];
  const halfWindow = Math.floor(windowSize / 2);
  
  for (let i = 0; i < data.length; i++) {
    let sum = 0;
    let count = 0;
    
    // Calculate average within the window
    for (let j = Math.max(0, i - halfWindow); j <= Math.min(data.length - 1, i + halfWindow); j++) {
      if (data[j] !== null && data[j] !== undefined) {
        sum += data[j];
        count++;
      }
    }
    
    smoothed[i] = count > 0 ? sum / count : data[i];
  }
  
  return smoothed;
};

/**
 * Exponential moving average smoothing (gentle smoothing)
 * @param {Array} data - Array of numeric values
 * @param {number} alpha - Smoothing factor (0-1, default: 0.3, lower = more smoothing)
 * @returns {Array} - Smoothed data array
 */
export const exponentialSmooth = (data, alpha = 0.3) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }
  
  const smoothed = [data[0]]; // Start with first value
  
  for (let i = 1; i < data.length; i++) {
    if (data[i] !== null && data[i] !== undefined) {
      smoothed[i] = alpha * data[i] + (1 - alpha) * smoothed[i - 1];
    } else {
      smoothed[i] = smoothed[i - 1];
    }
  }
  
  return smoothed;
};

/**
 * Gaussian smoothing (medium smoothing)
 * @param {Array} data - Array of numeric values
 * @param {number} sigma - Standard deviation for Gaussian kernel (default: 1)
 * @returns {Array} - Smoothed data array
 */
export const gaussianSmooth = (data, sigma = 1) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }
  
  // Create Gaussian kernel
  const kernelSize = Math.ceil(sigma * 3) * 2 + 1;
  const kernel = [];
  const center = Math.floor(kernelSize / 2);
  let sum = 0;
  
  for (let i = 0; i < kernelSize; i++) {
    const x = i - center;
    const value = Math.exp(-(x * x) / (2 * sigma * sigma));
    kernel[i] = value;
    sum += value;
  }
  
  // Normalize kernel
  for (let i = 0; i < kernelSize; i++) {
    kernel[i] /= sum;
  }
  
  // Apply convolution
  const smoothed = [];
  for (let i = 0; i < data.length; i++) {
    let weightedSum = 0;
    let totalWeight = 0;
    
    for (let j = 0; j < kernelSize; j++) {
      const dataIndex = i - center + j;
      if (dataIndex >= 0 && dataIndex < data.length && data[dataIndex] !== null && data[dataIndex] !== undefined) {
        weightedSum += data[dataIndex] * kernel[j];
        totalWeight += kernel[j];
      }
    }
    
    smoothed[i] = totalWeight > 0 ? weightedSum / totalWeight : data[i];
  }
  
  return smoothed;
};

/**
 * Savitzky-Golay smoothing (preserves peaks and valleys)
 * @param {Array} data - Array of numeric values
 * @param {number} windowSize - Window size (must be odd, default: 5)
 * @param {number} degree - Polynomial degree (default: 2)
 * @returns {Array} - Smoothed data array
 */
export const savitzkyGolaySmooth = (data, windowSize = 5, degree = 2) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [...data];
  }
  
  // Ensure window size is odd
  if (windowSize % 2 === 0) windowSize += 1;
  
  const halfWindow = Math.floor(windowSize / 2);
  const smoothed = [...data];
  
  // Simplified Savitzky-Golay for degree 2 (quadratic)
  // Using pre-calculated coefficients for common window sizes
  const coefficients = {
    5: [-3, 12, 17, 12, -3], // normalized by 35
    7: [-2, 3, 6, 7, 6, 3, -2], // normalized by 21
    9: [-21, 14, 39, 54, 59, 54, 39, 14, -21] // normalized by 231
  };
  
  const coeff = coefficients[windowSize];
  if (!coeff) {
    // Fallback to moving average for unsupported window sizes
    return movingAverageSmooth(data, windowSize);
  }
  
  const normalizer = coeff.reduce((sum, c) => sum + c, 0);
  
  for (let i = halfWindow; i < data.length - halfWindow; i++) {
    let sum = 0;
    for (let j = 0; j < windowSize; j++) {
      sum += data[i - halfWindow + j] * coeff[j];
    }
    smoothed[i] = sum / normalizer;
  }
  
  return smoothed;
};

export function formatDuration(seconds, isTextual = false, roundAfterInMinutes) {
  if (seconds < 0) {
      throw new Error("Duration cannot be negative");
  }

  const hours = Math.round(Math.floor(seconds / 3600));
  const minutes = Math.round(Math.floor((seconds % 3600) / 60));
  let remainingSeconds = Math.round(seconds % 60);

  if (roundAfterInMinutes != null && seconds / 60 > roundAfterInMinutes) {
    remainingSeconds = 0;
  }

  if (isTextual) {
    if (hours > 0) {
      if (minutes === 0 && remainingSeconds === 0) {
        return `${hours} hr`;
      } else if (remainingSeconds === 0) {
        return `${hours} hr ${minutes} min`;
      } else {
        return `${hours} hr ${minutes} min ${remainingSeconds} sec`;
      }
    } else if (minutes > 0) {
      if (remainingSeconds === 0) {
        return `${minutes} min`;
      } else {
        return `${minutes} min ${remainingSeconds} sec`;
      }
    } else {
      return `${remainingSeconds} sec`;
    }
  } else {
    const pad = (num) => num.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`;
  }
}

export function normalizeGeos(countries, bringToTop = [], includeBlockedGeos=false) {
  const overrides = {
    UM: "US Outlying",
    IO: "BIOT",
    TF: "Fr. S. Terr.",
    HM: "HIMI",
    SH: "Saint Helena",
    KN: "St Kitts & N.",
    PM: "St P. & Miq.",
    VC: "St Vincent & G.",
    SJ: "Svalbard & JM",
    TC: "Turks & Caicos",
    AG: "Antigua-Barb.",
    BA: "Bosnia & Herz",
    ST: "São Tomé & Pr.",
    CF: "CAR",
    AE: "UAE",
    GB: "UK",
    TR: "Turkey",
    TL: "East Timor",
    CD: "Congo (DRC)",
    CG: "Congo (Rep.)",
    GS: "South Georgia",
    BQ: "Caribbean NL",
    CX: "Christmas Is.",
    DO: "Dom. Rep.",
    GQ: "Eq. Guinea",
    FK: "Falkland Is.",
    PF: "Fr. Polynesia",
    MP: "Mariana Is.",
    KY: "Cayman Is.",
    MH: "Marshall Is.",
    SB: "Solomon Is.",
    WF: "Wallis & Fut.",
    VG: "BVI",
    VI: "USVI",
    BL: "St Barthélemy",
    PG: "PNG",
    TT: "Trinidad",
    MK: "Macedonia",
    VC: "St Vincent",
    ST: "São Tomé",
    TC: "Turks",
    NF: "Norfolk Is.",
    EH: "W. Sahara",
    AS: "Am. Samoa",
    // Keep if you like an explicit pin for parity with the baseline:
    US: "United States"
  };
  const blockedGeos = ["AF", "CF", "CD", "CU", "ER", "ET", "HT", "IR", "LB", "LY", "ML", "MM", "NI", "KP", "RU", "SO", "SS", "SD", "SY", "VE", "YE", "ZW"];

  let topItems = [];
  let remainingItems = [];
  countries.forEach(({ id, name }) => {
    const base = name
      .replace(/\s*\([^)]*\)/g, "") // remove anything in parentheses
      .replace(/\b(State|Republic|Federation|People's|Democratic|Kingdom|Province)\b/gi, "")
      .replace(/\b(of|the)\b/gi, "")
      .replace(/,\s*/g, " ")
      .replace(/\s{2,}/g, " ")
      .trim();

    const suggestion = overrides[id];
    const finalLabel =
      suggestion && suggestion.length < name.length ? suggestion : base;
    if (bringToTop.includes(id)) {
      topItems.push({ value:id, label: finalLabel });
    } else if (includeBlockedGeos || !blockedGeos.includes(id)) {
      remainingItems.push({ value:id, label: finalLabel });
    }
  });
  return [...topItems, ...remainingItems];
}